<template lang="pug">
  .b-user-select-popup
    h2._title {{ title || 'Контакты' }}
    BaseInput(v-model="searchString" label="Поиск по имени или e-mail")
    BaseTabs._tabs(:tabs="$options.tabs" big grey v-model="activeTab" v-if="multiple")
    ._users
      EmptyList(v-if="!usersFiltered.length") {{ emptyTitle || 'Нет контактов' }}
      ._user(v-for="user in usersFiltered" :key="user['@id']")
        UserTile(:user="user")
        ._status
          ._selected(v-if="isSelectedExternal(user)") Выбран
          Btn(sm grey transparent v-else-if="isSelectedInternal(user)" @click="uncheck(user)") Отменить
          Btn(sm green v-else @click="check(user)") Выбрать
    ._buttons
      Btn(@click="close(true)") Сохранить
      Btn(@click="close(false)" grey) Отмена
</template>

<script>
import BaseInput from '@/components/BaseInput.vue';
import BaseTabs from '@/components/BaseTabs.vue';
import UserTile from '@/components/UserTile.vue';
import EmptyList from '@/components/ui/EmptyList.vue';

/**
 * Выбор пользователя (или нескольких) в виде wrappable-попапа: открывается
 * через popupModel.add({ wrappable: true, component, params }), параметры
 * приходят в init(), результат отдаётся событием finish — как у остальных
 * попапов проекта.
 *
 * params:
 *   selected   — IRI уже выбранных, они помечаются как «Выбран» и
 *                недоступны для повторного выбора
 *   multiple   — множественный выбор, добавляет вкладки фильтра
 *   users      — свой список пользователей; без него берутся контакты
 *                текущего пользователя из стора
 *   title      — заголовок попапа
 *   emptyTitle — текст пустого списка
 *
 * finish: при multiple — массив выбранных пользователей, иначе один
 * пользователь; при отмене — false.
 *
 * TODO: это замена UserSelectPopup2, который открывается через ref/args и
 * рисует собственный BasePopup. Логика здесь скопирована, а не унаследована,
 * чтобы компоненты не были связаны. В будущем всю систему нужно перевести на
 * этот компонент, а UserSelectPopup2 удалить — на момент написания он ещё
 * используется в ProjectMembers, DepartmentTile, CompanyDepartmentListPage
 * и TeamMemberFormPopupWrappable.
 */
export default {
  name: 'UserSelectPopupWrappable',
  components: { BaseInput, BaseTabs, UserTile, EmptyList },
  tabs: [
    { value: 'all', label: 'Все' },
    { value: 'checked', icon: 'checked' },
    { value: 'unchecked', icon: 'unchecked' },
  ],
  data() {
    return {
      activeTab: 'all',
      searchString: '',
      multiple: false,
      // IRI выбранных снаружи — их менять нельзя
      selectedExternal: [],
      // выбранное в этом попапе: массив IRI при multiple, иначе объект
      selectedInternal: null,
      // свой список пользователей вместо контактов из стора
      customUserSet: null,
      title: '',
      emptyTitle: '',
    };
  },
  computed: {
    contacts() {
      return this.customUserSet ?? this.$store.getters['contact/users'];
    },
    usersFiltered() {
      return this.contacts.filter((user) => {
        const searchString = this.searchString.toLowerCase().replace(/ /g, '');
        const userSearchString = ((user.name ?? '') + (user.surname ?? '') + user.email).toLowerCase();
        const fitsBySearchString = !searchString || userSearchString.includes(searchString);

        const userIsSelected = this.isSelectedExternal(user) || this.isSelectedInternal(user);
        const fitsByTabs = this.activeTab === 'all'
          || (this.activeTab === 'checked' && userIsSelected)
          || (this.activeTab === 'unchecked' && !userIsSelected);

        return fitsBySearchString && fitsByTabs;
      });
    },
  },
  methods: {
    init(params = {}) {
      this.multiple = params.multiple ?? false;
      this.selectedExternal = params.selected ?? [];
      this.selectedInternal = this.multiple ? [] : null;
      this.customUserSet = params.users ?? null;
      this.title = params.title ?? '';
      this.emptyTitle = params.emptyTitle ?? '';

      // контакты нужны, только когда список не передали снаружи
      if (!this.customUserSet) this.$store.dispatch('contact/fetchItems');
    },
    isSelectedExternal(user) {
      return this.selectedExternal.includes(user['@id']);
    },
    isSelectedInternal(user) {
      return this.multiple
        ? this.selectedInternal.includes(user['@id'])
        : this.selectedInternal?.['@id'] === user['@id'];
    },
    check(user) {
      this.selectedInternal = this.multiple
        ? [...this.selectedInternal, user['@id']]
        : user;
    },
    uncheck(user) {
      this.selectedInternal = this.multiple
        ? this.selectedInternal.filter(iri => iri !== user['@id'])
        : null;
    },
    close(saveResult) {
      if (!saveResult) {
        this.$emit('finish', false);
        return;
      }

      const result = this.multiple
        ? this.contacts.filter(user => this.isSelectedInternal(user))
        : this.contacts.find(user => this.isSelectedInternal(user));

      this.$emit('finish', result ?? false);
    },
  },
};
</script>

<style scoped lang="scss">
.user-select-popup {
  width: 450px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__title {
    font-size: 24px;
    line-height: 24px;
    color: var(--color-super-dark);
  }

  &__users {
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    gap: 13px;
    max-height: calc(100vh - 370px);
    height: 500px;
    padding-bottom: 7px;
  }

  &__user {
    display: flex;
    gap: 17px;
  }

  &__status {
    margin-left: auto;
    width: 150px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__selected {
    font-size: 12px;
    line-height: 15px;
    letter-spacing: 0.01em;
    color: #788CA1;
    font-weight: normal;
  }

  &__buttons {
    display: flex;
    justify-content: flex-end;
    gap: 20px;
  }
}
</style>
